/**
 * Copy and rough section outlines for public /devs/:slug placeholder pages (production URLs on www.quillai.app).
 *
 * @typedef {{
 *   title: string,
 *   summary: string,
 *   sections: ReadonlyArray<{ heading: string, blurb: string }>,
 * }} DevsPlaceholderContent
 */

/** @type {Readonly<Record<string, DevsPlaceholderContent>>} */
export const DEVS_PLACEHOLDER_BY_SLUG = {
  documentation: {
    title: "Documentation",
    summary:
      "Guides, tutorials, and workspace concepts for Quill AI. This hub will mirror what ships on www.quillai.app.",
    sections: [
      { heading: "Getting started", blurb: "Account setup, first project, and linking sessions to customers." },
      { heading: "Core concepts", blurb: "Customers, projects, templates, and how artifacts trace back to meetings." },
      { heading: "Editor & AI", blurb: "Drafting flow, review checkpoints, and template-driven structure." },
    ],
  },
  api: {
    title: "API",
    summary:
      "HTTP APIs and integration patterns for workspaces, documents, and (where available) webhooks—spec and SDK notes landing here.",
    sections: [
      { heading: "Authentication", blurb: "Keys, OAuth-style flows, and per-account scoping." },
      { heading: "Resource map", blurb: "Customers, projects, sessions, documents, and export endpoints." },
      { heading: "Rate limits & errors", blurb: "Standard error shapes, retry guidance, and versioning." },
    ],
  },
  security: {
    title: "Security",
    summary:
      "How we protect data in transit and at rest, access controls, and how to assess Quill AI alongside your infosec process.",
    sections: [
      { heading: "Data handling", blurb: "Encryption, retention tiers, and customer isolation." },
      { heading: "Compliance posture", blurb: "SOC-style summaries, subprocessors, and audit artifacts as they become available." },
      { heading: "Your responsibilities", blurb: "Shared model for template content, PII in transcripts, and admin roles." },
    ],
  },
  faq: {
    title: "FAQ",
    summary:
      "Short answers for evaluators and admins—billing, limits, transcription providers, and where product behavior is headed.",
    sections: [
      { heading: "Plans & billing", blurb: "Free tier, upgrades, and in-app Stripe management." },
      { heading: "Sessions & transcription", blurb: "Recording bots, imports, and language coverage." },
      { heading: "Templates & AI", blurb: "What the model sees, retention, and human review defaults." },
    ],
  },
  integrations: {
    title: "Integrations",
    summary:
      "Connect Quill AI to CRMs, call recorders, and document destinations—roadmap-first view of supported and planned connectors.",
    sections: [
      { heading: "Native & future connectors", blurb: "Prioritized third-party integrations and request channels." },
      { heading: "API & automation", blurb: "When to use Zapier-style glue versus first-class sync." },
      { heading: "Enterprise rollout", blurb: "SCIM, SSO, and custom integration reviews." },
    ],
  },
  "privacy-policy": {
    title: "Privacy policy",
    summary:
      "How we collect, use, and share personal data. Final legal text will be published here for Quill AI users and visitors.",
    sections: [
      { heading: "What we collect", blurb: "Account data, usage telemetry, and content you store in the workspace." },
      { heading: "How we use data", blurb: "Service delivery, improvement, and support—without selling your documents." },
      { heading: "Your choices", blurb: "Access, deletion, and regional considerations as the policy is finalized." },
    ],
  },
  "terms-of-service": {
    title: "Terms of service",
    summary:
      "Terms governing use of Quill AI products and websites. Final terms will replace this outline when ready for external use.",
    sections: [
      { heading: "Acceptable use", blurb: "Permitted use, restrictions, and enforcement." },
      { heading: "Subscriptions & liability", blurb: "Fees, disclaimers, and limitation frameworks." },
      { heading: "Disputes & law", blurb: "Governing law, arbitration or courts, and contact for legal notices." },
    ],
  },
};

/**
 * Valid slugs for the marketing /devs/:slug route.
 * @type {ReadonlySet<string>}
 */
export const DEVS_PLACEHOLDER_SLUGS = new Set(Object.keys(DEVS_PLACEHOLDER_BY_SLUG));

/**
 * @param {string} slug - URL segment after /devs/.
 * @returns {DevsPlaceholderContent | null} Content when slug is known; otherwise null.
 */
export function getDevsPlaceholderContent(slug) {
  return DEVS_PLACEHOLDER_BY_SLUG[slug] ?? null;
}
